import { useData } from "@hooks/useData";
import { ID } from "@models";
import http from "@core/fetch";
import { PermissionLevelEnum, PermissionSection } from "@models/PermissionSection";
import apiConfig from "@apiConfig";
import { components } from "@models/api/api";

type CategoryResponse = components["schemas"]["CategoryKeyMetricLtoModelViewModel"];
type LtoItem = components["schemas"]["SRLtoWithScoresDataViewModel"];

export interface CategoryLensChainLtoRow {
    id: number;
    name: string;
    date: string;
    link: string;
    unbrandedPi: number;
    unbrandedPiStar: number;
    uniqueness: number;
    uniquenessStar: number;
    frequency: number;
    frequencyStar: number;
    draw: number;
    drawStar: number;
    value: number;
    valueStar: number;
    score: number;
    viability: string;
    scorePermissionLevel?: PermissionLevelEnum;
}

interface CategoryLensChainLtosResult {
    permissionLevel: PermissionLevelEnum;
    rowsByCategory: Map<ID, Map<string, CategoryLensChainLtoRow[]>>;
}

const DEFAULT_DATA: CategoryLensChainLtosResult = {
    permissionLevel: PermissionLevelEnum.Full,
    rowsByCategory: new Map(),
};

const REQUEST_TYPE: keyof typeof apiConfig = 'get category lens chain top performing ltos';

export enum LtoMetricName {
    UnbrandedPi = 'Unbranded PI',
    Uniqueness = 'Uniqueness',
    Frequency = 'Frequency',
    Draw = 'Draw',
    Value = 'Value',
    Score = 'Score',
}

function getIndex(item: LtoItem, key: LtoMetricName) {
    return item.scoresData?.data?.indexes?.find(i => i.key === key);
}

function mapToRow(item: LtoItem): CategoryLensChainLtoRow {
    return {
        id: item.id ?? 0,
        name: item.name ?? '',
        date: item.date ?? '',
        link: item.link ?? '',
        unbrandedPi: getIndex(item, LtoMetricName.UnbrandedPi)?.value ?? 0,
        unbrandedPiStar: getIndex(item, LtoMetricName.UnbrandedPi)?.starsCount ?? 0,
        uniqueness: getIndex(item, LtoMetricName.Uniqueness)?.value ?? 0,
        uniquenessStar: getIndex(item, LtoMetricName.Uniqueness)?.starsCount ?? 0,
        frequency: getIndex(item, LtoMetricName.Frequency)?.value ?? 0,
        frequencyStar: getIndex(item, LtoMetricName.Frequency)?.starsCount ?? 0,
        draw: getIndex(item, LtoMetricName.Draw)?.value ?? 0,
        drawStar: getIndex(item, LtoMetricName.Draw)?.starsCount ?? 0,
        value: getIndex(item, LtoMetricName.Value)?.value ?? 0,
        valueStar: getIndex(item, LtoMetricName.Value)?.starsCount ?? 0,
        score: item.scoresData?.data?.score ?? 0,
        viability: item.scoresData?.data?.viability ?? '',
        scorePermissionLevel: PermissionSection.getPermissionLevel(item.scoresData?.permissionLevel),
    };
}

function mapCategoryToMetricRows(cat: CategoryResponse): Map<string, CategoryLensChainLtoRow[]> {
    const entries = (cat.keyMetricLtos || []).map(({ keyMetricName, ltos }) => {
        const rows = (ltos?.data ?? []).map(mapToRow);
        return [keyMetricName, rows] as [string, CategoryLensChainLtoRow[]];
    });

    return new Map(entries);
}

async function getHomeCategoryLensChainLtos(chainId: ID): Promise<CategoryLensChainLtosResult> {
    try {
        const response: CategoryResponse[] = await http.get(REQUEST_TYPE, { chainId });
        const rowsByCategory = new Map<ID, Map<string, CategoryLensChainLtoRow[]>>();

        for (const cat of response) {
            const rowsByMetric = mapCategoryToMetricRows(cat);
            rowsByCategory.set(cat.categoryId ?? 0, rowsByMetric);
        }

        const permissionLevel = response
            .flatMap(cat => cat.keyMetricLtos ?? [])
            .map(m => PermissionSection.getPermissionLevel(m.ltos?.permissionLevel))
            .some(l => l === PermissionLevelEnum.Full)
            ? PermissionLevelEnum.Full
            : PermissionLevelEnum.Limited;

        return { permissionLevel, rowsByCategory };
    } catch {
        return DEFAULT_DATA;
    }
}

export function useHomeCategoryLensChainLtos(chainId: ID) {
    return useData(DEFAULT_DATA, getHomeCategoryLensChainLtos, chainId);
}